import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '../lib/routes';
import { useAuthStore, useDmStore, useAgentStore, useServerStore } from '../stores';
import { useToast } from './Toast';
import {
  ApiError,
  DEFAULT_PROJECTION_STRATEGY,
  fetchAgent,
  fetchAgentConfig,
  getAgentChannels,
  fetchRemoteNodes,
  rotateAgentApiKey,
  revealAgentApiKey,
  fetchAgentRuntime,
  setAgentChannelProjectionStrategy,
  type Agent,
  type AgentChannelSummary,
  type AgentRuntime,
  type ProjectionStrategy,
  type HostedAgentPlacement,
} from '../lib/api';
import RuntimeCard from './RuntimeCard';
import { UserBadge } from './UserBadge';
import { UserAvatar } from './UserAvatar';
import { DISABLED_LABEL } from '../lib/agent-state';
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { buildAgentConnectCommandParts, providerHasPlatformDifference, resolveAgentProvider, type AgentProvider, type Platform } from './AgentConnectResult';
import { Tabs, Tooltip } from 'antd';
import { getBorgeeHttpBaseUrl } from '../lib/runtime-base-url';
import PresenceDot from './PresenceDot';
import { usePresence } from '../hooks/usePresence';
import { useAgentLifecycle, type AgentLifecycle } from '../hooks/useAgentLifecycle';
import ChannelView from './ChannelView';
import DecommissionAgentModal from './DecommissionAgentModal';
import AgentDisabledBanner from './AgentDisabledBanner';
import styles from './AgentDetailView.module.css';
import AgentLifecycleSection from './AgentLifecycleSection';
import { copyToClipboard, readClipboard } from '../lib/clipboard';

function formatMaskedApiKey(last4: string): string {
  return `bgr_...${last4}`;
}

const API_KEY_AUTO_CLEAR_MS = 60_000;
const PROJECTION_STRATEGIES: ProjectionStrategy[] = [
  'session-brief',
  'turn-full',
  'message-only',
  'turn-thin',
  'file-brief',
];

const PROJECTION_STRATEGY_INFOTIP = [
  'session-brief: stable Borgee projection lives mainly in the Claude session brief.',
  'turn-full: each turn carries the full Borgee projection inline.',
  'message-only: ordinary turns pass through mostly the raw user message.',
  'turn-thin: each turn keeps only a thin Borgee projection layer.',
  'file-brief: stable Borgee projection is written to a provider-visible CLAUDE.md brief.',
];

function resolveConnectedClaudeProvider(lastConnectedPluginId?: string): 'claude' | null {
  return lastConnectedPluginId === 'agents-host:claude' ? 'claude' : null;
}

// GET /agents/{id} answers 403 only for an agent someone else owns. Everything
// else it cannot serve is a 404, and that covers far more than a typo: the
// `/members/:id` route reaches this view for every colleague who is not the
// viewer, so a human account, a deleted agent and an unknown id all land here.
// Those two are settled answers; anything else is a failure that a second
// attempt can still turn into a profile.
interface ProfileFailure {
  text: string;
  retryable: boolean;
}

function describeProfileFailure(err: unknown): ProfileFailure {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return { text: 'Only its owner can see this agent.', retryable: false };
    }
    if (err.status === 404) {
      return { text: 'This member is a person, or an agent that no longer exists.', retryable: false };
    }
    return { text: err.message, retryable: true };
  }
  // A rejection that is not an ApiError never reached the server: it is the
  // browser's own TypeError, whose message is about fetch rather than about
  // this profile.
  return { text: 'Could not reach the server.', retryable: true };
}

interface Props {
  agentId: string;
  agent: Agent;
}

// Every piece of state below belongs to the agent the view is showing — the
// fetched profile, the DM channel, the revealed key — and none of it is reset in
// place, so the whole view is keyed on the agent id. Switching agents therefore
// mounts a fresh one instead of carrying the previous agent's data under the new
// agent's name, and no caller has to know that.
export default function AgentDetailView({ agentId, agent }: Props) {
  return <AgentDetail key={agentId} agentId={agentId} agent={agent} />;
}

function AgentDetail({ agentId, agent: agentProp }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'chat' | 'profile'>(tabParam === 'profile' ? 'profile' : 'chat');
  const [dmChannelId, setDmChannelId] = useState<string | null>(null);
  const [dmFailed, setDmFailed] = useState(false);
  const [fetchedAgent, setFetchedAgent] = useState<Agent | null>(null);
  const [profileFailure, setProfileFailure] = useState<ProfileFailure | null>(null);
  const [profileAttempt, setProfileAttempt] = useState(0);
  const [showDecommission, setShowDecommission] = useState(false);
  const openDm = useDmStore(s => s.openDm);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const agent = fetchedAgent ?? agentProp;

  // One authoritative copy of the agent for the whole view. The banner, the
  // profile hero and the lifecycle controls must never disagree about whether
  // the agent is switched off, so every write lands here.
  const applyAgent = useCallback((updated: Agent) => {
    setFetchedAgent(updated);
    // The sidebars render from the agent list, so a lifecycle write has to
    // reach it too or their rows keep the stale dot until the next navigation.
    useAgentStore.getState().loadAgents();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAgent(agentId)
      .then((loaded) => {
        if (cancelled) return;
        setFetchedAgent(loaded);
        setProfileFailure(null);
      })
      .catch((err: unknown) => { if (!cancelled) setProfileFailure(describeProfileFailure(err)); });
    return () => {
      cancelled = true;
    };
  }, [agentId, profileAttempt]);

  const lifecycle = useAgentLifecycle({
    agentId,
    agent,
    onUpdated: applyAgent,
    onToast: showToast,
  });

  const switchTab = (tab: 'chat' | 'profile') => {
    setActiveTab(tab);
    setSearchParams((params) => { params.set('tab', tab); return params; }, { replace: true });
  };

  useEffect(() => {
    const t = tabParam === 'profile' ? 'profile' : 'chat';
    setActiveTab(t);
  }, [tabParam]);

  useEffect(() => {
    let cancelled = false;
    openDm(agentId)
      .then((dm) => {
        if (!cancelled) setDmChannelId(dm.id);
      })
      .catch(() => { if (!cancelled) setDmFailed(true); });
    return () => {
      cancelled = true;
    };
  }, [agentId, openDm]);

  return (
    <div className="agent-detail-view">
      <div className="agent-detail-header">
        <UserBadge
          userId={agent.id}
          displayName={agent.display_name}
          role="agent"
          avatarUrl={agent.avatar_url}
          size={32}
          state={agent.state}
          disabled={agent.disabled}
        />
      </div>
      {showDecommission && (
        <DecommissionAgentModal
          agentId={agentId}
          agentName={agent.display_name}
          agentState={agent.state}
          lastConnectedPluginId={agent.last_connected_plugin_id}
          onClose={() => setShowDecommission(false)}
          onDeleted={() => {
            useAgentStore.getState().loadAgents();
            const guildId = useAuthStore.getState().guildId;
            if (guildId) void Promise.resolve(useServerStore.getState().loadMembers(guildId)).catch(() => undefined);
            showToast(`${agent.display_name} deleted`);
            if (guildId && window.location.pathname.includes('/members')) {
              navigate(routes.members(guildId));
            } else {
              navigate(routes.agents());
            }
          }}
        />
      )}

      <div className="channel-view-header">
        <div className="channel-view-tabs">
          <button
            className={`channel-view-tab${activeTab === 'chat' ? ' active' : ''}`}
            onClick={() => switchTab('chat')}
          >
            Chat
          </button>
          <button
            className={`channel-view-tab${activeTab === 'profile' ? ' active' : ''}`}
            onClick={() => switchTab('profile')}
          >
            Profile
          </button>
        </div>
      </div>

      <div className="agent-detail-content">
        <AgentDisabledBanner agentName={agent.display_name} lifecycle={lifecycle} />
        {activeTab === 'chat' && dmChannelId && <ChannelView channelId={dmChannelId} hideHeader composerDisabled={agent.disabled || agent.state === 'offline'} />}
        {activeTab === 'chat' && !dmChannelId && (
          <div className="no-channel">
            {dmFailed
              ? <p>{agent.role === 'agent' ? 'Agent is not connected yet. Once it connects, you can chat here.' : 'Cannot open chat with this member.'}</p>
              : <div className="loading-spinner-large" />
            }
          </div>
        )}
        {activeTab === 'profile' && (
          <div className="agent-settings-scroll">
            {profileFailure ? (
              // No dismiss: behind this panel is the caller's placeholder agent,
              // and clearing the failure would render that stand-in as though the
              // profile had loaded — the one outcome this branch exists to avoid.
              <div className="no-channel" data-testid="agent-profile-unavailable">
                <div className="agent-inline-error" role="alert">
                  <strong>Can't show this profile.</strong> {profileFailure.text}
                  {profileFailure.retryable && (
                    <div className="agent-inline-error-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        data-testid="agent-profile-retry"
                        onClick={() => setProfileAttempt(n => n + 1)}
                      >
                        Try again
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <AgentSettingsPanel
                agentId={agentId}
                agent={agent}
                lifecycle={lifecycle}
                onDecommission={() => setShowDecommission(true)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingsPanelProps {
  agentId: string;
  agent: Agent;
  lifecycle: AgentLifecycle;
  onDecommission: () => void;
}

function AgentSettingsPanel({ agentId, agent, lifecycle, onDecommission }: SettingsPanelProps) {
  const currentUser = useAuthStore(s => s.currentUser);
  const { showToast } = useToast();
  const navigate = useNavigate();
  // A rotate or reveal returns a fresher key than the loaded agent carries, so
  // it takes precedence until the agent is reloaded.
  const [revealedLast4, setRevealedLast4] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [runtime, setRuntime] = useState<AgentRuntime | null>(null);
  const [runtimeLoaded, setRuntimeLoaded] = useState(false);
  const [hostedPlacement, setHostedPlacement] = useState<HostedAgentPlacement | null>(null);
  const [hostedComputerName, setHostedComputerName] = useState<string | null>(null);
  const [hostingDetailsError, setHostingDetailsError] = useState<string | null>(null);
  const autoClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewerUserID = currentUser?.id ?? null;
  const isOwner = viewerUserID !== null && viewerUserID === agent.owner_id;
  const last4 = revealedLast4 ?? agent.api_key_last4 ?? null;
  const disabled = lifecycle.disabled;

  const loadRuntime = useCallback(async () => {
    try {
      const rt = await fetchAgentRuntime(agentId);
      setRuntime(rt);
    } catch {
      setRuntime(null);
    } finally {
      setRuntimeLoaded(true);
    }
  }, [agentId]);

  useEffect(() => {
    loadRuntime();
  }, [loadRuntime]);

  useEffect(() => {
    let cancelled = false;
    const loadHostingDetails = async () => {
      try {
        const { blob } = await fetchAgentConfig(agentId);
        if (cancelled) return;
        const hosted = blob.hosted ?? null;
        setHostedPlacement(hosted);
        setHostingDetailsError(null);
        if (!hosted) {
          setHostedComputerName(null);
          return;
        }
        const nodes = await fetchRemoteNodes();
        if (cancelled) return;
        setHostedComputerName(
          nodes.find((node) => node.id === hosted.node_id)?.machine_name ?? hosted.node_id,
        );
      } catch (err) {
        if (cancelled) return;
        setHostedPlacement(null);
        setHostedComputerName(null);
        setHostingDetailsError(
          err instanceof Error ? err.message : 'Could not load hosting details.',
        );
      }
    };
    void loadHostingDetails();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    return () => {
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
      }
    };
  }, []);

  const handleCopyKey = async () => {
    setCopying(true);
    try {
      const key = await revealAgentApiKey(agentId);
      if (typeof key !== 'string' || key.length === 0) {
        showToast('Copy failed');
        return;
      }
      const ok = await copyToClipboard(key);
      if (!ok) return;
      setRevealedLast4(key.slice(-4));
      showToast('API Key copied, auto-clears in 60s');
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
          }
        } catch {}
      }, API_KEY_AUTO_CLEAR_MS);
    } catch {
      showToast('Copy failed');
    } finally {
      setCopying(false);
    }
  };

  const handleRotateKey = async () => {
    try {
      const { api_key: key } = await rotateAgentApiKey(agentId);
      await copyToClipboard(key);
      setRevealedLast4(key.slice(-4));
      showToast('New API Key copied');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed');
    }
  };

  return (
    <div className="agent-page">
      <AgentProfileHero
        agent={agent}
        disabled={disabled}
        hostedPlacement={hostedPlacement}
        hostedComputerName={hostedComputerName}
      />

      {(hostedPlacement || hostingDetailsError) && (
        <div className="agent-profile-field" data-testid="agent-hosted-details">
          <div className="agent-profile-field-label">HOSTED BY COMPUTER</div>
          {hostingDetailsError ? (
            <div className="agent-inline-note">{hostingDetailsError}</div>
          ) : (
            <>
              <div
                className="agent-profile-field-value"
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <span style={{ flex: 1 }}>{hostedComputerName ?? hostedPlacement?.node_id}</span>
                {hostedPlacement && (
                  <button
                    className="btn btn-sm"
                    data-testid="agent-open-computer"
                    onClick={() => navigate(routes.computer(hostedPlacement.node_id))}
                  >
                    Open computer page
                  </button>
                )}
              </div>
              <div className="agent-inline-note">
                Managed by this computer through {hostedPlacement?.provider === 'claude'
                  ? 'Claude Code'
                  : hostedPlacement?.provider === 'codex'
                    ? 'Codex'
                    : 'GitHub Copilot'}.
              </div>
            </>
          )}
        </div>
      )}

      {/* Copy stays live while disabled and Rotate does not: the UI blocks
          exactly what the server refuses. Revealing a key is a read, but the
          schema aborts any write of a new api_key onto a disabled row, so a
          live Rotate could only produce a 500. */}
      <div className="agent-profile-field">
        <div className="agent-profile-field-label">API KEY</div>
        <div
          className="agent-profile-field-value"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 14, flex: 1 }}>
            {last4 ? formatMaskedApiKey(last4) : 'Loading...'}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm" onClick={handleCopyKey} disabled={copying}>
              {copying ? 'Copying...' : 'Copy'}
            </button>
            <button
              className="btn btn-sm"
              data-testid="agent-rotate-key"
              onClick={handleRotateKey}
              disabled={disabled}
              title={disabled ? 'Enable the agent before rotating its key' : undefined}
            >
              Rotate
            </button>
          </div>
        </div>
        {disabled && (
          <div className="agent-inline-note">
            The key can still be copied, but it will not authenticate while {agent.display_name} is disabled, and it cannot be rotated until you enable it again.
          </div>
        )}
      </div>

      <ConnectInstructions
        agentName={agent.display_name}
        agentId={agentId}
        lastConnectedPluginId={agent.last_connected_plugin_id}
        disabled={disabled}
        hostedPlacement={hostedPlacement}
        hostedComputerName={hostedComputerName}
      />

      {((hostedPlacement?.provider ?? resolveConnectedClaudeProvider(agent.last_connected_plugin_id)) === 'claude') && (
        <ProjectionStrategyCard agentId={agentId} />
      )}

      {runtimeLoaded && runtime && (
        <section className="agent-detail-card agent-detail-card-runtime">
          <RuntimeCard
            agent={agent}
            runtime={runtime}
            viewerUserID={viewerUserID}
            onRefresh={loadRuntime}
          />
        </section>
      )}

      <AgentLifecycleSection
        agentName={agent.display_name}
        isOwner={isOwner}
        lifecycle={lifecycle}
        onDecommission={onDecommission}
      />
    </div>
  );
}

interface ConnectProps {
  agentName: string;
  agentId: string;
  lastConnectedPluginId?: string;
  disabled: boolean;
  hostedPlacement: HostedAgentPlacement | null;
  hostedComputerName: string | null;
}

function ConnectInstructions({
  agentName,
  agentId,
  lastConnectedPluginId,
  disabled,
  hostedPlacement,
  hostedComputerName,
}: ConnectProps) {
  const [activeProvider, setActiveProvider] = useState<AgentProvider>(() => resolveAgentProvider(agentId, lastConnectedPluginId));
  const [pcPlatform, setPcPlatform] = useState<Platform>('windows');
  const serverUrl = getBorgeeHttpBaseUrl();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [expandedWhileDisabled, setExpandedWhileDisabled] = useState(false);

  useEffect(() => {
    revealAgentApiKey(agentId).then(setApiKey).catch(() => {});
  }, [agentId]);

  useEffect(() => {
    if (!disabled) setExpandedWhileDisabled(false);
  }, [disabled]);

  const cmdBlocks = apiKey
    ? buildAgentConnectCommandParts(activeProvider, serverUrl, apiKey, agentName, pcPlatform)
    : ['Loading...'];

  const tabs: { key: AgentProvider; label: string }[] = [
    { key: 'claude', label: 'Claude Code' },
    { key: 'copilot', label: 'GitHub Copilot' },
    { key: 'codex', label: 'Codex' },
    { key: 'dsh', label: 'DeepSeek Harness' },
    { key: 'openclaw', label: 'OpenClaw' },
    { key: 'hermes', label: 'Hermes Agent' },
    { key: 'praestoclaw', label: 'PraestoClaw' },
    { key: 'kosmos', label: 'Kosmos Desktop' },
  ];

  if (hostedPlacement) {
    return (
      <div className="agent-profile-field" data-testid="agent-connect-instructions">
        <div className="agent-profile-field-label">HOSTED RUNTIME</div>
        <div className="agent-profile-field-value">
          {hostedComputerName ?? hostedPlacement.node_id}
        </div>
        <div className="agent-inline-note">
          {agentName} is managed on that computer through {hostedPlacement.provider === 'claude'
            ? 'Claude Code'
            : hostedPlacement.provider === 'codex'
              ? 'Codex'
              : 'GitHub Copilot'}. Use the computer flow to create or remove hosted agents instead of running a manual connect command here.
        </div>
      </div>
    );
  }

  // Collapse rather than delete while disabled. A disabled agent's key is
  // refused at connect time, so leaving a live "run this now" command in place
  // invites the owner to paste something that must fail and looks like a bug.
  // Deleting it outright would break the legitimate case of getting the
  // command ready before enabling the agent again.
  if (disabled && !expandedWhileDisabled) {
    return (
      <div className="agent-profile-field" data-testid="agent-connect-instructions">
        <div className="agent-profile-field-label">CONNECT</div>
        <div className="agent-connect-collapsed">
          Connection instructions are hidden while {agentName} is disabled — a disabled agent's API key is refused at connect time, so the command below cannot bring it online. Enable it again first.
          <div className="agent-connect-collapsed-actions">
            <button
              className="btn btn-sm"
              data-testid="agent-connect-show-anyway"
              onClick={() => setExpandedWhileDisabled(true)}
            >
              Show instructions anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-profile-field" data-testid="agent-connect-instructions">
      <div className="agent-profile-field-label">CONNECT</div>
      {disabled && (
        <div className="agent-inline-note" style={{ marginBottom: 10 }}>
          {agentName} is disabled — this command will be refused until you enable it again.
        </div>
      )}
      <div className={styles.providerTabs}>
        {tabs.map(t => (
          <button
            key={t.key}
            className={`btn btn-sm${activeProvider === t.key ? ' btn-primary' : ''}`}
            onClick={() => setActiveProvider(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {providerHasPlatformDifference(activeProvider) && (
        <div style={{ marginBottom: 4 }}>
          <Tabs
            size="small"
            activeKey={pcPlatform}
            onChange={(key: string) => setPcPlatform(key as Platform)}
            items={[
              { key: 'windows', label: 'Windows' },
              { key: 'linux', label: 'Linux / macOS' },
            ]}
          />
        </div>
      )}
      {cmdBlocks.map((block, i) => (
        <div key={i} style={{ position: 'relative', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 6, padding: 10, fontSize: 12, fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 8 }}>
          {block}
          {apiKey && (
            <button
              className="btn btn-sm"
              style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, padding: '2px 6px' }}
              onClick={() => copyToClipboard(block)}
            >
              <CopyOutlined />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ProjectionStrategyCard({ agentId }: { agentId: string }) {
  const { showToast } = useToast();
  const [channels, setChannels] = useState<AgentChannelSummary[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ProjectionStrategy>>({});
  const [loading, setLoading] = useState(true);
  const [savingChannelId, setSavingChannelId] = useState<string | null>(null);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getAgentChannels(agentId);
      setChannels(rows);
      setDrafts(
        Object.fromEntries(
          rows.map(channel => [channel.id, channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY]),
        ),
      );
    } catch (err) {
      setChannels([]);
      setDrafts({});
      showToast(err instanceof Error ? err.message : 'Could not load projection strategy');
    } finally {
      setLoading(false);
    }
  }, [agentId, showToast]);

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  const handleSave = async (channelId: string) => {
    const strategy = drafts[channelId];
    if (!strategy) {
      return;
    }
    setSavingChannelId(channelId);
    try {
      await setAgentChannelProjectionStrategy(agentId, channelId, strategy);
      await loadChannels();
      showToast('Projection strategy saved');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not save projection strategy');
    } finally {
      setSavingChannelId(null);
    }
  };

  return (
    <section
      className="agent-detail-card agent-detail-card-claude-channel-strategy"
      data-testid="agent-projection-strategy"
    >
      <div
        className="agent-profile-field-label"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <span>PROJECTION STRATEGY</span>
        <Tooltip
          title={(
            <div style={{ display: 'grid', gap: 4 }}>
              {PROJECTION_STRATEGY_INFOTIP.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
        >
          <span
            aria-label="Projection strategy descriptions"
            data-testid="agent-projection-strategy-infotip"
            title={PROJECTION_STRATEGY_INFOTIP.join('\n')}
            style={{ display: 'inline-flex', color: 'var(--text-muted)', cursor: 'help' }}
          >
            <InfoCircleOutlined />
          </span>
        </Tooltip>
      </div>
      <div className="agent-inline-note" style={{ marginTop: 4 }}>
        One setting per top-level channel. Task threads inherit from the parent channel.
      </div>
      {loading ? (
        <div className="agent-inline-note" style={{ marginTop: 8 }}>Loading…</div>
      ) : channels.length === 0 ? (
        <div className="agent-inline-note" style={{ marginTop: 8 }}>
          Add this agent to a channel to manage projection strategy.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {channels.map((channel) => {
            const savedStrategy = channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY;
            const draftStrategy = drafts[channel.id] ?? savedStrategy;
            const saving = savingChannelId === channel.id;
            return (
              <div
                key={channel.id}
                style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: 8, alignItems: 'center' }}
              >
                <span>#{channel.name}</span>
                <select
                  className="input-field"
                  value={draftStrategy}
                  onChange={(event) => setDrafts(prev => ({
                    ...prev,
                    [channel.id]: event.target.value as ProjectionStrategy,
                  }))}
                  aria-label={`Projection strategy for ${channel.name}`}
                >
                  {PROJECTION_STRATEGIES.map((strategy) => (
                    <option key={strategy} value={strategy}>{strategy}</option>
                  ))}
                </select>
                <button
                  className="btn btn-sm"
                  disabled={saving || draftStrategy === savedStrategy}
                  onClick={() => handleSave(channel.id)}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// The hero carries the agent's state, and delete is not here: it lives at the
// foot of the profile behind the lifecycle disclosure, so an AI colleague
// cannot be removed with one stray click on a bare icon.
function AgentProfileHero({
  agent,
  disabled,
  hostedPlacement,
  hostedComputerName,
}: {
  agent: Agent;
  disabled: boolean;
  hostedPlacement: HostedAgentPlacement | null;
  hostedComputerName: string | null;
}) {
  const live = usePresence(agent.id);
  const agentState = live?.state ?? agent.state;
  const reason = live?.reason ?? agent.reason;

  return (
    <>
      <div className="agent-profile-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
          <UserAvatar
            userId={agent.id}
            displayName={agent.display_name}
            avatarUrl={agent.avatar_url}
            size={64}
            role="agent"
            state={agent.state}
            disabled={disabled}
          />
          <div className="agent-profile-info">
            <div className="agent-profile-name">
              {agent.display_name}
              {disabled
                ? <span className="agent-state-chip agent-state-chip-disabled">{DISABLED_LABEL}</span>
                : <PresenceDot state={agentState} reason={reason} />}
              {hostedPlacement && (
                <span className="agent-state-chip agent-state-chip-hosted" data-testid="agent-hosted-badge">
                  Hosted
                </span>
              )}
            </div>
            <div className="agent-profile-slug">@{agent.id}</div>
          </div>
        </div>
      </div>

      <div className="agent-profile-field">
        <div className="agent-profile-field-label">DISPLAY NAME</div>
        <div className="agent-profile-field-value">{agent.display_name}</div>
      </div>

      <div className="agent-profile-field">
        <div className="agent-profile-field-label">DESCRIPTION</div>
        <div className="agent-profile-field-value agent-profile-field-dim">
          {(agent as any).description || 'No description'}
        </div>
      </div>

    </>
  );
}
